import React from "react";
import { ControllerRenderProps, FieldError } from "react-hook-form";
import { NumericOptions, ValidationConstraints } from "@/components/tasks/types";
import ValidationEditor from "./shared/ValidationEditor";

interface NumericInputProps {
  name?: string;
  options: NumericOptions;
  field: ControllerRenderProps;
  error?: FieldError;
  readOnly?: boolean;
}

export default function NumericInput({
  name,
  options,
  field,
  error,
  readOnly,
}: NumericInputProps) {
  const minVal = options?.validation?.min;
  const maxVal = options?.validation?.max;
  const showAsSlider = options?.showAsSlider || false;
  const step = options?.step ?? "any";

  return (
    <div className="space-y-2">
      {showAsSlider ? (
        <div className="space-y-3">
          <input
            {...field}
            type="range"
            min={minVal}
            max={maxVal}
            step={step}
            className="w-full"
            disabled={readOnly}
            list="tickmarks"
          />
          {/* generate datalist of ticks at step, if step is specified */}
          {
            step !== "any" && minVal !== undefined && maxVal !== undefined && (
              <datalist id="tickmarks">
                {Array.from(
                  { length: Math.floor((maxVal - minVal) / Number(step)) + 1 },
                  (_, i) => minVal + i * Number(step)
                ).map((val) => (
                  <option key={val} value={val} />
                ))}
              </datalist>
            )
          }
          <div className="flex justify-between items-center">
            <span className="text-gray-600 font-medium">
              {field.value || minVal || 0}
            </span>
            {minVal !== undefined && maxVal !== undefined && (
              <span className="text-sm text-gray-500">
                Range: {minVal} - {maxVal}
              </span>
            )}
          </div>
        </div>
      ) : (
        <input
          {...field}
          type="number"
          min={minVal}
          max={maxVal}
          step={step}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            error ? "border-red-500" : "border-gray-300"
          }`}
          placeholder={`Enter ${name?.toLowerCase()}...`}
          readOnly={readOnly}
        />
      )}
      {error && <p className="text-sm text-red-600 mt-1">{error.message}</p>}
      {(minVal || maxVal) && !showAsSlider && (
        <p className="text-sm text-gray-500">
          {minVal !== undefined &&
            maxVal === undefined &&
            `Value must be greater than or equal to ${minVal}`}
          {maxVal !== undefined &&
            minVal === undefined &&
            `Value must be less than or equal to ${maxVal}`}
          {minVal !== undefined &&
            maxVal !== undefined &&
            `Value must be between ${minVal} and ${maxVal}`}
        </p>
      )}
    </div>
  );
}

interface NumericInputEditorProps {
  options: NumericOptions;
  validation: ValidationConstraints;
  onOptionsChange: (options: NumericOptions) => void;
  onValidationChange: (validation: ValidationConstraints) => void;
}

NumericInput.Editor = function NumericInputEditor({
  options,
  validation,
  onOptionsChange,
  onValidationChange,
}: NumericInputEditorProps) {
  return (
    <div className="space-y-3">
      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="show-as-slider"
          checked={options?.showAsSlider || false}
          onChange={(e) => {
            onOptionsChange({
              ...options,
              showAsSlider: e.target.checked,
            });
          }}
          className="text-blue-600 focus:ring-blue-500 rounded"
        />
        <label
          htmlFor="show-as-slider"
          className="text-sm font-medium text-gray-700"
        >
          Show as Slider
        </label>
      </div>
      <p className="text-xs text-gray-500 mt-1">
          Use the minimum and maximum values in the validation rules to define the range.
          Defaults to 0-100 range if min/max not set.
      </p>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Step Value
        </label>
        <input
          type="number"
          min="0"
          step="any"
          value={options?.step ?? ""}
          onChange={(e) => {
            onOptionsChange({
              ...options,
              step: parseFloat(e.target.value) || undefined,
            });
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="e.g., 1 for integers, 0.1 for decimals"
        />
        <p className="text-xs text-gray-500 mt-1">
          Optional. Defines increment/decrement steps. Leave empty for any value.
        </p>
      </div>
      <ValidationEditor
        validation={validation}
        promptType="numeric"
        onValidationChange={onValidationChange}
      />
    </div>
  );
};
